//
// Copyright (c) 2019-Present Syncari - All rights reserved.
// Wrapper for the Condition so it can be used inside Filter
//
import ObjectID from 'bson-objectid';
import cx from 'classnames';
import { useEffect, useMemo, useState } from 'react';

import Condition, { ConditionProps } from 'components/inputs/condition';
import { IconButton } from 'design-system';
import { ConditionValue, LeftValue, RightOption, PicklistValue, PicklistValues } from 'components/inputs/types';
import AppConstants from 'utils/AppConstants';
import { tNamespaced } from 'utils/i18nUtil';

import FilterTokenSelector from './FilterTokenSelector';

import './FilterCondition.less';

const tnf = tNamespaced('Filter');

export interface FilterConditionValue extends ConditionValue {
  predicateId?: string;
}

export interface FilterConditionProps {
  name: string;
  defaultValue: FilterConditionValue;
  onAdd?: (predicateId: string) => void;
  onDelete?: (predicateId: string) => void;
  onChange?: (value: FilterConditionValue, name?: string, values?: any) => void;
  displayMode?: string;
  className?: string;
  fieldValues: LeftValue[];
  fetchPicklistValues: ConditionProps['fetchPicklistValues'];
  picklistValues: PicklistValues<PicklistValue[]>;
  operatorType?: string;
  rightType?: string;
  singleCondition?: boolean;
  leftRenderType?: string;
  lhsDisabled?: boolean;
  allowUserToken?: boolean;
  rightValues?: RightOption[];
  context?: string;
  hideLeftInput?: boolean;
  verticalLayout?: boolean;
  allFieldsDisabled?: boolean;
  placeHolder?: string;
  fieldValueOptions?: JSX.Element[];
  /** Forces the data-token selector off regardless of the left field's datatype - for contexts with no node to pull tokens from (e.g. schema studio's validation rules) */
  disableTokens?: boolean;
}

const FilterCondition = ({
  name,
  defaultValue,
  onAdd,
  onDelete,
  onChange,
  displayMode,
  className,
  fieldValues,
  fetchPicklistValues,
  picklistValues,
  operatorType,
  rightType,
  singleCondition = false,
  leftRenderType,
  lhsDisabled,
  allowUserToken,
  rightValues,
  context,
  hideLeftInput,
  verticalLayout,
  allFieldsDisabled,
  placeHolder,
  fieldValueOptions,
  disableTokens,
}: FilterConditionProps) => {
  const [predicateId] = useState(defaultValue?.predicateId || ObjectID.generate());
  const [value, setValue] = useState(defaultValue);
  const [active, setActive] = useState(false);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const filter = useMemo(() => {
    if (!predicateId) {
      return <div />;
    }
    const isReadonly = displayMode === AppConstants.INPUT_DISPLAY_MODE.READONLY;
    return (
      <div
        className={cx(
          'synri-filter-condition',
          'synri-filter-v2',
          {
            active: active && !isReadonly && !singleCondition,
            'with-textarea': value?.left?.datatype === AppConstants.INPUT_TYPE.TEXTAREA,
            'synri-filter-condition-readonly': isReadonly,
            'single-condition': singleCondition,
          },
          className
        )}
        onMouseEnter={() => setActive(true)}
        onMouseLeave={() => setActive(false)}>
        <div key={predicateId} className="filter-condition-container">
          <div className="filter-input-container">
            <Condition
              name={name}
              lhsDisabled={lhsDisabled}
              values={fieldValues}
              defaultValue={value}
              fetchPicklistValues={fetchPicklistValues}
              picklistValues={picklistValues}
              displayMode={displayMode}
              onChange={(newVal: ConditionValue) => onChange?.(newVal)}
              operatorType={operatorType}
              rightType={rightType}
              leftRenderType={leftRenderType}
              allowUserToken={allowUserToken}
              rightValues={rightValues}
              context={context}
              hideLeftInput={hideLeftInput}
              verticalLayout={verticalLayout}
              allFieldsDisabled={allFieldsDisabled}
              placeHolder={placeHolder}
              leftOptions={fieldValueOptions}
              rightTokenSelectorRenderer={(onTokenSelect) => <FilterTokenSelector onTokenSelect={onTokenSelect} />}
              disableTokens={disableTokens}
            />
          </div>
          {!isReadonly && !singleCondition && !allFieldsDisabled && (
            <div className="filter-condition-actions">
              {onAdd && (
                <IconButton
                  variant="tertiary"
                  size="medium"
                  icon="actionable/nest-condition"
                  aria-label={tnf('add_nested_condition_label')}
                  tooltip={tnf('add_nested_condition_label')}
                  onClick={() => onAdd(predicateId)}
                />
              )}
              {onDelete && (
                <IconButton
                  variant="negative-tertiary"
                  size="medium"
                  icon="actionable/delete-bin"
                  aria-label={tnf('delete_condition_label')}
                  tooltip={tnf('delete_condition_label')}
                  onClick={() => onDelete(predicateId)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }, [
    predicateId,
    displayMode,
    active,
    singleCondition,
    allowUserToken,
    value,
    className,
    context,
    name,
    lhsDisabled,
    fieldValues,
    fetchPicklistValues,
    picklistValues,
    operatorType,
    rightType,
    leftRenderType,
    onAdd,
    onChange,
    onDelete,
    hideLeftInput,
    verticalLayout,
    allFieldsDisabled,
    placeHolder,
    fieldValueOptions,
    disableTokens,
  ]);

  return filter;
};

export default FilterCondition;
